import { useMemo, useState, useRef, useEffect } from "react";
import { Box, useToken, Heading, HStack, Select, Text } from "@chakra-ui/react";
import { useDisclosure, IconButton } from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import html2canvas from "html2canvas-pro";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import OpenEnded from "./OpenEnded.js";
import { findBottomNObjects, findTopNObjects } from "../../utils/filter.js";
import { useParams } from "react-router";

const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7f50",
  "#8dd1e1",
  "#a4de6c",
];

export const CustomBarLabel = (props) => {
  const { x, y, width, height, value } = props;

  const middleX = x + width / 2;
  const middleY = y + height / 2;

  return (
    <g>
      <text
        x={middleX}
        y={middleY}
        fill="#fff"
        fontSize={10}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value.toFixed(1)} %
      </text>
    </g>
  );
};

const formatter = (value) => {
  const stringValue = value.toString();
  const trimmedValue = stringValue.substring(0, 50);
  return stringValue.length > 50 ? `${trimmedValue}...` : trimmedValue;
};

const StandardBarChart = ({
  data = [],
  height = 300,
  width = "100%",
  title = "",
  children,
  rank
}) => {
  const { client } = useParams();
  const [red] = useToken("colors", ["red.500"]);
  const [green] = useToken("colors", ["green.500"]);
  const rankedItems = useMemo(() => {
    if (!data || !rank) return [];
    const { type, count } = rank;
    if (type === "top") {
      return findTopNObjects(data, "value", count);
    } else if (type === "bottom") {
      return findBottomNObjects(data, "value", count);
    }

    return [];
  }, [data, rank]);

  const cumulativePercent = useMemo(() => {
    return rankedItems.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [rankedItems]);

  const chartRef = useRef();
  const [chartType, setChartType] = useState("bar"); // "bar" or "pie"

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <HStack justifyContent="space-between">
        <Heading size="sm">{title}</Heading>
        <HStack>
          {" "}
          <Select
            size="xs"
            minW="70px"
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value={"bar"}>Bar</option>
            <option value={"pie"}>Pie</option>
          </Select>
          <IconButton
            icon={<FiDownload />}
            size="xs"
            variant="outline"
            onClick={async () => {
              const canvas = await html2canvas(chartRef.current);
              const url = canvas.toDataURL("image/png");
              const a = document.createElement("a");
              a.href = url;
              a.download = `${title}.png`;
              a.click();
            }}
            aria-label="Download chart"
          />
          {children}
        </HStack>
      </HStack>
      <Box ref={chartRef}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        {chartType === "bar" ? (
          <>
            <ResponsiveContainer height={height} width={width} position="relative">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 20, right: 20, bottom: 20, left: 75 }}
              >
                <Tooltip />
                <XAxis
                  type="number"
                  domain={client === "2" ? [0, 100] : undefined}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={client === "2" ? 160 : 120}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatter}
                  interval={client === "2" ? 0 : undefined}
                  style={{ fontSize: "0.8rem", textAlign: "left" }}
                />
                <Bar
                  dataKey="value"
                  barSize={client === "2" ? 30 : 40}
                  radius={[4, 4, 4, 4]}
                  label={<CustomBarLabel noSuffix />}
                >
                  {data.map((entry, idx) => {
                    const isIncludedInRank = rankedItems.some(
                      (item) => item.name === entry.name
                    );
                    return (
                      <Cell
                        key={`cell-${idx}`}
                        fill={isIncludedInRank ? green : red}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Text
              mt="2"
              fontSize="sm"
              fontWeight="medium"
              textAlign="center"
              height="20px"
            >
              {rankedItems.length > 1 && (
                `${rank.type === "top" ? "Top" : "Bottom"} ${rank.count} cumulative percentage: ${cumulativePercent.toFixed(1)}%`
              )}
            </Text>
          </>
        ) : (
          <PieChart width={300} height={300}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {data.map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>
            <Legend />
          </PieChart>
        )}
      </Box>
    </Box>
  );
};

const QuestionDisplay = ({ question, question_type, data, rank }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (question_type === "long_text" || question_type === "numeric_input") {
    return <OpenEnded question={question} answers={data} />;
  } else if (question_type === "multiple_choice") {
    return <StandardBarChart data={data} title={question} rank={rank} />;
  } else {
    return (
      <pre>{JSON.stringify({ question, question_type, data }, null, 2)}</pre>
    );
  }
};

export default QuestionDisplay;
